import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { useHistoryDiagnosed } from '@hooks/register/history/use-history-diagnosed';
import { useLocalize } from '@hooks/use-localize';
import { useScrollToHash } from '@hooks/use-scroll-to-hash';
import { useStepForm } from '@hooks/use-step-form';
import { asString, asStringBool } from '@/utils/parse';
import { createHistoryDiagnosedSchema } from '@views/register/history/history-diagnosed.schema';
import { RadioField } from '@views/register/history/history-fields';
import { DashRegistrationStep, historyLinkMap } from '@/types/dashboard';
import type { IHistoryDiagnosedFormValues, IHistoryDiagnosedModel, IHistoryDiagnosedSubmitResult } from '@/types/history/history-diagnosed';
import CardiovascularSection from '@views/register/history/diagnosed-sections/cardiovascular-section';
import RespiratorySection from '@views/register/history/diagnosed-sections/respiratory-section';
import ConditionsSection from '@views/register/history/diagnosed-sections/conditions-section';
import WomensHealthSection from '@views/register/history/diagnosed-sections/womens-health-section';
import OtherConditionsSection from '@views/register/history/diagnosed-sections/other-conditions-section';
import ScreeningsSection from '@views/register/history/diagnosed-sections/screenings-section';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';

const HistoryDiagnosed = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useHistoryDiagnosed(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <HistoryDiagnosedContent model={model} submit={submit} isSubmitting={isSubmitting} submitError={submitError} />
        </PageLoader>
    );
};

interface IHistoryDiagnosedContentProps {
    model: IHistoryDiagnosedModel;
    submit: (values: IHistoryDiagnosedFormValues) => Promise<IHistoryDiagnosedSubmitResult>;
    isSubmitting: boolean;
    submitError: string | null;
}

const HistoryDiagnosedContent = ({ model, submit, isSubmitting, submitError }: IHistoryDiagnosedContentProps) => {
    const t = useLocalize(model.languageData);

    useScrollToHash();

    const form = useStepForm<IHistoryDiagnosedFormValues, IHistoryDiagnosedSubmitResult>({
        resolver: zodResolver(createHistoryDiagnosedSchema(model)) as unknown as Resolver<IHistoryDiagnosedFormValues>,
        defaultValues: {
            aboType: asString(model.aboType),
            hasHistoryOfHighBp: asString(model.hasHistoryOfHighBp),
            highBpAgents: asString(model.highBpAgents),
            hadHighBpDuringPregnancy: asString(model.hadHighBpDuringPregnancy),
            hasHadCabg: asString(model.hasHadCabg),
            hasHistoryOfBloodClots: asString(model.hasHistoryOfBloodClots),
            hasHistoryOfCoronaryDisease: asString(model.hasHistoryOfCoronaryDisease),
            hasHistoryOfHighCholOrTg: asString(model.hasHistoryOfHighCholOrTg),
            hasStents: asString(model.hasStents),
            historyChestPainExercise: asString(model.historyChestPainExercise),
            historyChestPainOther: asString(model.historyChestPainOther),
            resultsOfClimbingStairs: asString(model.resultsOfClimbingStairs),
            hadStressTestWithinYear: asString(model.hadStressTestWithinYear),
            stressTestLocation: model.stressTestLocation,
            stressTestResults: asString(model.stressTestResults),
            hasHadStroke: asString(model.hasHadStroke),
            howManyStrokes: asString(model.howManyStrokes),
            hasHadLegBypass: asString(model.hasHadLegBypass),
            howManyLegBypasses: asString(model.howManyLegBypasses),
            historyLungDisease: asString(model.historyLungDisease),
            hasCopd: asString(model.hasCopd),
            hasTb: asString(model.hasTb),
            historyPneumonia: asString(model.historyPneumonia),
            hasRespiratoryIssues: asString(model.hasRespiratoryIssues),
            respiratoryIssuesDescription: model.respiratoryIssuesDescription,
            hasGastroIssues: asString(model.hasGastroIssues),
            gastroIssuesDescription: model.gastroIssuesDescription,
            historyGallstones: asString(model.historyGallstones),
            historyPancreatitis: asString(model.historyPancreatitis),
            historyLiverDisease: asString(model.historyLiverDisease),
            hasHepB: asString(model.hasHepB),
            hasHepC: asString(model.hasHepC),
            hasBleedingProblem: asString(model.hasBleedingProblem),
            hasHadUti: asString(model.hasHadUti),
            utiEpisodes: asString(model.utiEpisodes),
            utiRemissionStatus: asString(model.utiRemissionStatus),
            historyKidneyDamage: asString(model.historyKidneyDamage),
            historyProteinuria: asString(model.historyProteinuria),
            historyHematuria: asString(model.historyHematuria),
            historyNeuroDisease: asString(model.historyNeuroDisease),
            historySeizure: asString(model.historySeizure),
            hasLupus: asString(model.hasLupus),
            historyParalysis: asString(model.historyParalysis),
            hasArthritis: asString(model.hasArthritis),
            historyNeuropathy: asString(model.historyNeuropathy),
            historyDepression: asString(model.historyDepression),
            historyAnxiety: asString(model.historyAnxiety),
            hasHistoryOfPsychiatric: asString(model.hasHistoryOfPsychiatric),
            psychiatricDescription: model.psychiatricDescription,
            historyFibroidUterus: asString(model.historyFibroidUterus),
            historyEndometriosis: asString(model.historyEndometriosis),
            historyPolycysticOvaries: asString(model.historyPolycysticOvaries),
            hasHadMiscarriages: asString(model.hasHadMiscarriages),
            howManyMiscarriages: asString(model.howManyMiscarriages),
            hadLateMiscarriage: asStringBool(model.hadLateMiscarriage),
            hasHadAbortions: asString(model.hasHadAbortions),
            howManyAbortions: asString(model.howManyAbortions),
            hasReceivedTransfusion: asString(model.hasReceivedTransfusion),
            howManyTransfusions: asString(model.howManyTransfusions),
            hasHistoryOfObesity: asString(model.hasHistoryOfObesity),
            hasFabrys: asString(model.hasFabrys),
            hasSickleCell: asString(model.hasSickleCell),
            hasSickleCellTrait: asString(model.hasSickleCellTrait),
            hasSarcoidosis: asString(model.hasSarcoidosis),
            hasAutoImmune: asString(model.hasAutoImmune),
            autoImmuneDescription: model.autoImmuneDescription,
            hasHadPhysicalExam: asString(model.hasHadPhysicalExam),
            physicalExamYearsAgo: asString(model.physicalExamYearsAgo),
            hasHadMammogram: asString(model.hasHadMammogram),
            mammogramYearsAgo: asString(model.mammogramYearsAgo),
            hasHadPapSmear: asString(model.hasHadPapSmear),
            papSmearYearsAgo: asString(model.papSmearYearsAgo),
            hasHadPsaTest: asString(model.hasHadPsaTest),
            psaTestYearsAgo: asString(model.psaTestYearsAgo),
            hasHadColonoscopy: asString(model.hasHadColonoscopy),
            colonoscopyYearsAgo: asString(model.colonoscopyYearsAgo),
            hasAnimalBiteExposure: asString(model.hasAnimalBiteExposure),
            animalBiteDescription: model.animalBiteDescription,
            hasActiveInfection: asString(model.hasActiveInfection),
            activeInfectionDescription: model.activeInfectionDescription,
            additionalComments: model.additionalComments,
        },
        submit,
        linkMap: historyLinkMap,
    });

    return (
        <StepFormLayout
            kind="history"
            model={model}
            step={DashRegistrationStep.HistoryDiagnosed}
            title={t('DashLabel_MedicalHistory', 'Medical History')}
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            <div id="cardiovascular">
                <div className='section-heading' style={{ paddingTop: 0 }}>Blood Type</div>

                <RadioField
                    name="aboType"
                    label={t('DashLabel_WhatSYourBloodType', "What's your blood type")}
                    options={model.aboTypeOptions}
                />

                <CardiovascularSection model={model} t={t} />
            </div>

            <div id="respiratory">
                <RespiratorySection model={model} t={t} />
            </div>

            <div id="conditions">
                <ConditionsSection model={model} t={t} />
            </div>

            {model.isFemale && (
                <div id="womens-health">
                    <WomensHealthSection model={model} t={t} />
                </div>
            )}

            <div id="other-conditions">
                <OtherConditionsSection model={model} t={t} />
            </div>

            <div id="screenings">
                <ScreeningsSection model={model} t={t} />
            </div>
        </StepFormLayout>
    );
};

export default HistoryDiagnosed;
